/** SUNTRONIX ONE — Global Universal Search (Cmd/Ctrl+K from every screen). */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import {
  Search, Command, Clock, Star, X, Building2, Users, FileText, Inbox, Package, Receipt, Cpu,
  Laptop, Boxes, Truck, LifeBuoy, UserRound, MapPin, Sparkles, Trash2,
} from "lucide-react";
import StatusBadge from "./StatusBadge";

const ICONS = { customers: Building2, companies: Building2, contacts: Users, quotations: FileText, customer_pos: Inbox,
  orders: Package, invoices: Receipt, products: Cpu, assets: Laptop, stock: Boxes, shipments: Truck, tickets: LifeBuoy,
  people: UserRound, locations: MapPin };
const NL = /(assigned to|warranty expir|available|pickup)/i;
const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
const fmtMeta = (m) => typeof m === "number" ? `₹${m.toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : m;

export default function CommandBar({ testId }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [interp, setInterp] = useState(null);
  const [suggest, setSuggest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen((o) => !o); }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    setTimeout(() => inputRef.current?.focus(), 40);
    api.get("/global-search/suggest").then(({ data }) => setSuggest(data)).catch(() => setSuggest(null));
  }, [open]);

  useEffect(() => {
    const t = q.trim();
    if (!open || t.length < 2) { setData(null); setInterp(null); return; }
    const h = setTimeout(async () => {
      setLoading(true);
      try {
        const [{ data: gs }, nl] = await Promise.all([
          api.get("/global-search/", { params: { q: t } }),
          NL.test(t) ? api.get("/search/universal", { params: { q: t } }).catch(() => null) : Promise.resolve(null),
        ]);
        setData(gs); setInterp(nl?.data?.interpretation || null); setCursor(0);
      } catch { setData(null); }
      finally { setLoading(false); }
    }, 160);
    return () => clearTimeout(h);
  }, [q, open]);

  const flat = useMemo(() => (data?.groups || []).flatMap((g) => g.items.map((it) => ({ ...it, group: g.label }))), [data]);

  const openItem = useCallback((it) => {
    setOpen(false); setQ("");
    if (it.route) navigate(it.route);
    api.post("/global-search/visit", { entity_type: it.type || it.entity_type, entity_id: it.id || it.entity_id,
      title: it.title, subtitle: it.subtitle || null, route: it.route || null }).catch(() => {});
  }, [navigate]);

  const onInputKey = (e) => {
    if (!flat.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, flat.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); const it = flat[cursor]; if (it) openItem(it); }
  };

  useEffect(() => { document.querySelector(`[data-gs-index="${cursor}"]`)?.scrollIntoView({ block: "nearest" }); }, [cursor]);

  const clearHistory = async () => { await api.delete("/global-search/history"); setSuggest((s) => ({ ...s, recent_searches: [] })); };

  return (
    <>
      <button type="button" data-testid={testId} onClick={() => setOpen(true)}
        className="w-full flex items-center gap-2 h-9 px-3 rounded-md border border-[color:var(--st-border)] bg-[color:var(--st-surface-alt)] hover:bg-white hover:border-slate-300 transition-colors text-left">
        <Search className="h-4 w-4 text-slate-400" strokeWidth={1.6} />
        <span className="text-sm text-slate-500 truncate">Search customers, quotes, orders, serials, tickets…</span>
        <span className="ml-auto hidden md:flex items-center gap-1 text-[10px] text-slate-400 border border-slate-200 rounded px-1.5 py-0.5 bg-white font-mono">
          {isMac ? <Command className="h-3 w-3" strokeWidth={2} /> : "Ctrl"}K
        </span>
      </button>

      {open && (
        createPortal(
<div className="fixed inset-0 z-palette bg-slate-900/40 backdrop-blur-[2px] flex items-start justify-center pt-4 sm:pt-20 px-2" onClick={() => setOpen(false)} data-testid="gs-overlay">
          <div className="bg-white w-full max-w-2xl rounded-lg shadow-2xl border border-slate-200 overflow-hidden animate-fade-in max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 px-3 border-b border-slate-100">
              <Search className="h-4 w-4 text-slate-400 shrink-0" />
              <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onInputKey} data-testid="gs-input"
                placeholder="Search or ask SUNTRONIX ONE…" className="flex-1 h-12 text-sm outline-none bg-transparent" />
              {loading && <Sparkles className="h-4 w-4 text-sky-500 animate-pulse" />}
              {q && <button onClick={() => setQ("")} className="text-slate-400 hover:text-slate-700" data-testid="gs-clear"><X className="h-4 w-4" /></button>}
              <kbd className="hidden sm:inline text-[10px] text-slate-400 border border-slate-200 rounded px-1.5 py-0.5 font-mono">esc</kbd>
            </div>

            <div className="overflow-y-auto p-2 space-y-3" data-testid="gs-results">
              {q.trim().length < 2 && <SuggestBlock s={suggest} onPick={(t) => setQ(t)} onOpen={openItem} onClear={clearHistory} />}
              {interp && <InterpretationBlock i={interp} onClose={() => setOpen(false)} />}
              {q.trim().length >= 2 && data && flat.length === 0 && !loading && (
                <div className="p-6 text-sm text-slate-500 text-center" data-testid="gs-no-matches">No matches for “{q}”.</div>
              )}
              {data?.fuzzy && flat.length > 0 && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1" data-testid="gs-fuzzy-note">Showing close matches — no exact results for “{q}”.</div>
              )}
              {(data?.groups || []).map((g) => {
                const Icon = ICONS[g.key] || Search;
                return (
                  <div key={g.key} data-testid={`gs-group-${g.key}`}>
                    <div className="flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-400 px-2 pb-1">
                      <Icon className="h-3 w-3" strokeWidth={2} /> {g.label} <span className="font-normal">({g.items.length})</span>
                    </div>
                    {g.items.map((it) => {
                      const idx = flat.findIndex((f) => f.type === it.type && f.id === it.id);
                      return (
                        <button key={`${g.key}-${it.id}`} type="button" data-gs-index={idx} data-testid={`gs-item-${g.key}-${it.id}`}
                          onMouseEnter={() => setCursor(idx)} onClick={() => openItem(it)}
                          className={`w-full text-left px-3 py-2 rounded flex items-center gap-3 ${idx === cursor ? "bg-sky-50" : "hover:bg-slate-50"}`}>
                          <span className="text-sm text-slate-900 truncate max-w-[45%]">{it.title}</span>
                          {it.subtitle && <span className="text-xs text-slate-500 truncate flex-1">{it.subtitle}</span>}
                          {it.meta != null && <span className="font-mono text-[11px] text-slate-400 truncate max-w-[25%]">{fmtMeta(it.meta)}</span>}
                          {it.status && <StatusBadge value={it.status} />}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
              {data && flat.length > 0 && (
                <div className="text-[10px] text-slate-400 px-2 pt-1 border-t border-slate-100 flex items-center gap-3">
                  <span>{data.total} results · {data.took_ms} ms</span><span className="ml-auto">↑↓ navigate · ↵ open · esc close</span>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body)
      )}
    </>
  );
}

function SuggestBlock({ s, onPick, onOpen, onClear }) {
  if (!s) return <div className="p-4 text-xs text-slate-400" data-testid="gs-suggest">Type at least 2 characters — customer, quote, PO, invoice, order, product, SKU, serial, IMEI, AWB, ticket, employee or location.</div>;
  const has = s.recent_searches?.length || s.recent_records?.length || s.frequent_records?.length;
  if (!has) return <div className="p-4 text-xs text-slate-400" data-testid="gs-suggest">Your recent searches and frequently opened records will appear here.</div>;
  const Rec = ({ items, testId }) => items.map((r) => (
    <button key={r.id || r.entity_id} onClick={() => onOpen(r)} data-testid={`${testId}-${r.entity_id}`}
      className="w-full text-left px-3 py-1.5 rounded hover:bg-slate-50 flex items-center gap-2 text-sm">
      <span className="text-slate-800 truncate">{r.title}</span>
      {r.subtitle && <span className="text-xs text-slate-400 truncate">{r.subtitle}</span>}
      {r.visits > 1 && <span className="ml-auto font-mono text-[10px] text-slate-400">{r.visits}×</span>}
    </button>
  ));
  return (
    <div className="space-y-3" data-testid="gs-suggest">
      {s.recent_searches?.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-400 px-2 pb-1">
            <Clock className="h-3 w-3" /> Recent searches
            <button onClick={onClear} className="ml-auto normal-case tracking-normal font-normal hover:text-slate-700 inline-flex items-center gap-1" data-testid="gs-clear-history"><Trash2 className="h-3 w-3" />clear</button>
          </div>
          <div className="flex flex-wrap gap-1.5 px-2">
            {s.recent_searches.map((r) => (
              <button key={r.q} onClick={() => onPick(r.q)} data-testid={`gs-recent-q-${r.q}`}
                className="text-[12px] rounded-full border border-slate-200 px-2.5 py-1 hover:bg-slate-50 text-slate-700">{r.q}</button>
            ))}
          </div>
        </div>
      )}
      {s.frequent_records?.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-400 px-2 pb-1"><Star className="h-3 w-3" /> Frequently accessed</div>
          <Rec items={s.frequent_records} testId="gs-frequent" />
        </div>
      )}
      {s.recent_records?.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-400 px-2 pb-1"><Clock className="h-3 w-3" /> Recently opened</div>
          <Rec items={s.recent_records} testId="gs-recent" />
        </div>
      )}
    </div>
  );
}

const InterpretationChip = ({ v }) => <span className="font-mono text-xs bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">{v}</span>;

function InterpretationBlock({ i, onClose }) {
  const K = InterpretationChip;
  if (i.kind === "assets_of_employee") {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-amber-700 mb-1">Interpreted as</div>
        <div className="text-sm text-slate-900">Devices assigned to <span className="font-semibold">{i.employee?.name}</span> — {i.assets.length}</div>
        <div className="mt-2 grid grid-cols-1 gap-1">
          {i.assets.map((a) => <div key={a.id} className="text-xs text-slate-700 flex items-center gap-2"><K v={a.asset_tag} /> {a.brand} {a.model} · <K v={a.serial} /> <StatusBadge value={a.lifecycle_state} /></div>)}
        </div>
      </div>
    );
  }
  if (i.kind === "warranty_expiring") {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-amber-700 mb-1">Interpreted as</div>
        <div className="text-sm text-slate-900">Warranty expiring in ≤ {i.days} days — {i.assets.length} devices</div>
      </div>
    );
  }
  if (i.kind === "available_devices") {
    return (
      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-emerald-700 mb-1">Interpreted as</div>
        <div className="text-sm text-slate-900">Available inventory — {i.assets.length} devices</div>
      </div>
    );
  }
  if (i.kind === "pickups_delayed") {
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 p-3">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-rose-700 mb-1">Interpreted as</div>
        <div className="text-sm text-slate-900">Pickups delayed &gt; 5 days — {i.pickups.length} pickups</div>
      </div>
    );
  }
  return null;
}

